/**
 * 硬體序號名單 (/roster, boards/Roster.dc.html) — admin+. Server-filtered, server-paged L3
 * roster: quick views, a filter column (search / origin / status / team / import batch / added
 * date), origin badges (derived server-side), CSV export of the current filter, bulk revoke and
 * a row → machine-lookup drawer. Filters live in the URL so dashboard / batch links land
 * pre-filtered.
 *
 * Contract note: GET /api/whitelist takes ONE origin and ONE status, so the board's checkbox
 * groups behave as single-select (none ticked = all).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Pager } from '@/components/Pager';
import { OriginTag, StatusText, Swatch } from '@/components/RosterBits';
import { TextArea, TextInput } from '@/components/Field';
import { errMsg } from '@/components/Dialogs';
import { useLookup } from '@/contexts/LookupContext';
import { usePoll } from '@/lib/usePoll';
import { api, BULK_REVOKE_MAX, isApiError } from '@/lib/api';
import { SEARCH_TOO_LONG, chunk, searchTooLong } from '@/lib/searchLimit';
import { dayToUtcMs, utcDay } from '@/lib/dates';
import { fmtDay } from '@/lib/format';
import { useDebounced } from '@/lib/useDebounced';
import { ORIGIN_LABEL, ORIGIN_ORDER, STATUS_LABEL, STATUS_ORDER } from '@/lib/labels';
import type {
  Paged,
  RosterEntry,
  RosterFacets,
  RosterOrigin,
  RosterQuery,
  RosterSort,
  RosterStats,
  RosterStatusFilter,
} from '@/types';

const ORIGINS = new Set<string>(ORIGIN_ORDER);
const STATUSES = new Set<string>([...STATUS_ORDER, 'expiring7']);
const SORTS = new Set<string>(['added_desc', 'added_asc', 'sn', 'expires']);
const DEFAULT_LIMIT = 100;

function readQuery(sp: URLSearchParams): Required<Pick<RosterQuery, 'page' | 'limit' | 'sort'>> & RosterQuery {
  const origin = sp.get('origin') ?? '';
  const status = sp.get('status') ?? '';
  const sort = sp.get('sort') ?? '';
  const page = Math.max(1, Number(sp.get('page')) || 1);
  const limit = Math.min(500, Math.max(1, Number(sp.get('limit')) || DEFAULT_LIMIT));
  return {
    q: sp.get('q') ?? undefined,
    origin: ORIGINS.has(origin) ? (origin as RosterOrigin) : undefined,
    status: STATUSES.has(status) ? (status as RosterStatusFilter) : undefined,
    team: sp.get('team') ?? undefined,
    batch: sp.get('batch') ?? undefined,
    from: sp.get('from') ?? undefined,
    to: sp.get('to') ?? undefined,
    sort: SORTS.has(sort) ? (sort as RosterSort) : 'added_desc',
    page,
    limit,
  };
}

export function Roster() {
  const [sp, setSp] = useSearchParams();
  const query = readQuery(sp);
  const lookup = useLookup();

  // The search box is local so typing doesn't rewrite the URL per keystroke.
  const [qInput, setQInput] = useState(query.q ?? '');
  const qDebounced = useDebounced(qInput.trim(), 350);
  const qTooLong = searchTooLong(qInput);
  useEffect(() => {
    if (searchTooLong(qDebounced)) return; // the Worker would 400; keep the last good search
    if ((query.q ?? '') !== qDebounced) setParams({ q: qDebounced || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);

  function setParams(patch: Record<string, string | null | undefined>, keepPage = false) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!keepPage) next.delete('page');
    setSp(next, { replace: true });
  }

  const key = sp.toString();
  const list = usePoll<Paged<RosterEntry>>(() => api.listRoster(query), 30_000, [key, lookup.changeTick]);
  const stats = usePoll<RosterStats>(() => api.rosterStats(), 30_000, [lookup.changeTick]);
  const facets = usePoll<RosterFacets>(() => api.rosterFacets(), 120_000);

  const rows = list.data?.entries ?? [];
  const total = list.data?.total ?? 0;
  const s = stats.data;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => setSelected(new Set()), [key]);
  const toggle = (sn: string) =>
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(sn)) n.delete(sn);
      else n.add(sn);
      return n;
    });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.sn));

  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const exportQuery = { q: query.q, origin: query.origin, status: query.status, team: query.team, batch: query.batch, from: query.from, to: query.to, sort: query.sort };

  const quick: { label: string; n: number | undefined; on: boolean; apply: () => void }[] = [
    { label: '全部', n: s?.total, on: !query.origin && !query.status, apply: () => setParams({ origin: null, status: null }) },
    { label: '已授權機器(序號兌換)', n: s?.by_origin.serial, on: query.origin === 'serial' && !query.status, apply: () => setParams({ origin: 'serial', status: null }) },
    { label: '7 天內到期', n: s?.expiring_7d, on: query.status === 'expiring7' && !query.origin, apply: () => setParams({ status: 'expiring7', origin: null }) },
    { label: '已撤銷', n: s?.by_status.revoked, on: query.status === 'revoked' && !query.origin, apply: () => setParams({ status: 'revoked', origin: null }) },
    { label: '無法判定來源', n: s?.by_origin.unknown, on: query.origin === 'unknown' && !query.status, apply: () => setParams({ origin: 'unknown', status: null }) },
  ];

  const batchName = useMemo(() => {
    const b = facets.data?.batches.find((x) => x.id === query.batch);
    return b?.name ?? query.batch;
  }, [facets.data, query.batch]);

  const describe = [
    `來源:${query.origin ? ORIGIN_LABEL[query.origin] : '全部'}`,
    `狀態:${query.status ? (query.status === 'expiring7' ? '7 天內到期' : STATUS_LABEL[query.status]) : '全部'}`,
    query.team ? `團隊:${query.team}` : '',
    query.batch ? `批次:${batchName}` : '',
    query.from || query.to ? `加入 ${query.from ?? '…'} – ${query.to ?? '…'}` : '',
  ].filter(Boolean).join(' · ');

  const sortHeader = (label: string, asc: RosterSort, desc?: RosterSort) => {
    const on = query.sort === asc || query.sort === desc;
    const next = desc ? (query.sort === desc ? asc : desc) : asc;
    const arrow = on ? (desc ? (query.sort === desc ? ' ↓' : ' ↑') : ' ↑') : '';
    return (
      <button type="button" className={'th-btn' + (on ? ' on' : '')} onClick={() => setParams({ sort: next === 'added_desc' ? null : next })}>
        {label}{arrow}
      </button>
    );
  };

  return (
    <div className="v2-page">
      <div className="v2-head" style={{ alignItems: 'center' }}>
        <div className="titles"><h1 className="v2-title">硬體序號名單</h1></div>
        <a className="btn btn-outline" href={api.rosterExportUrl(exportQuery)} download>
          匯出目前結果(CSV)
        </a>
        <Link className="btn btn-primary" to="/roster/import">匯入…</Link>
        <Button className="btn-outline" onClick={() => setAddOpen(true)}>新增一台</Button>
      </div>

      <div className="pills" role="group" aria-label="快速檢視">
        {quick.map((p) => (
          <button key={p.label} type="button" className={'pill' + (p.on ? ' on' : '')} onClick={p.apply}>
            {p.label} <span className="n">{p.n ?? ''}</span>
          </button>
        ))}
      </div>

      <div className="roster-body">
        <aside className="filter-aside" aria-label="篩選">
          <label className="v2-field">搜尋
            <input
              className="v2-input"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="序號、末四碼、擁有人、Email、團隊"
              spellCheck={false}
              aria-invalid={qTooLong || undefined}
            />
            {qTooLong && <span className="error-line" role="alert">{SEARCH_TOO_LONG}</span>}
          </label>
          <fieldset>
            <legend>來源(單選,不勾 = 全部)</legend>
            {ORIGIN_ORDER.map((o) => (
              <label key={o} className="opt-row">
                <input type="checkbox" checked={query.origin === o} onChange={() => setParams({ origin: query.origin === o ? null : o })} />
                <Swatch origin={o} />
                <span className="grow">{ORIGIN_LABEL[o]}</span>
                <span className="n">{s?.by_origin[o] ?? ''}</span>
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>狀態(單選,不勾 = 全部)</legend>
            {STATUS_ORDER.map((st) => (
              <label key={st} className="opt-row">
                <input type="checkbox" checked={query.status === st} onChange={() => setParams({ status: query.status === st ? null : st })} />
                <span className="grow">{STATUS_LABEL[st]}</span>
                <span className="n">{s?.by_status[st] ?? ''}</span>
              </label>
            ))}
          </fieldset>
          <label className="v2-field">團隊
            <select className="v2-select" value={query.team ?? ''} onChange={(e) => setParams({ team: e.target.value || null })}>
              <option value="">全部團隊</option>
              {(facets.data?.teams ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
              {query.team && !(facets.data?.teams ?? []).includes(query.team) && <option value={query.team}>{query.team}</option>}
            </select>
          </label>
          <label className="v2-field">匯入批次
            <select className="v2-select" value={query.batch ?? ''} onChange={(e) => setParams({ batch: e.target.value || null })}>
              <option value="">全部批次</option>
              {(facets.data?.batches ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              {query.batch && !(facets.data?.batches ?? []).some((b) => b.id === query.batch) && <option value={query.batch}>{query.batch}</option>}
            </select>
          </label>
          <div className="v2-field">加入日期
            <div className="date-range">
              <input className="v2-input" type="date" aria-label="開始日期" value={query.from ?? ''} onChange={(e) => setParams({ from: e.target.value || null })} />
              <span>–</span>
              <input className="v2-input" type="date" aria-label="結束日期" value={query.to ?? ''} onChange={(e) => setParams({ to: e.target.value || null })} />
            </div>
          </div>
          <button type="button" className="soft-btn" onClick={() => { setQInput(''); setSp(new URLSearchParams(), { replace: true }); }}>
            清除篩選
          </button>
        </aside>

        <section className="panel flush tpanel" style={{ flex: 1 }}>
          <div className="tpanel-bar">
            <span className="grow">符合 <strong>{list.data ? total : '…'}</strong> 台({describe})</span>
            <span>已選 {selected.size}</span>
            <button type="button" className="btn btn-outline-danger" disabled={selected.size === 0} onClick={() => setBulkOpen(true)}>
              撤銷所選
            </button>
          </div>
          {Boolean(list.error) && !list.data && <div className="error-line" style={{ padding: 'var(--space-3) var(--space-4)' }}>載入失敗:{errMsg(list.error)}</div>}
          <div className="tscroll">
            <table className="v2-table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      aria-label="全選本頁"
                      checked={allOnPage}
                      onChange={() => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.sn)))}
                    />
                  </th>
                  <th>{sortHeader('硬體序號', 'sn')}</th>
                  <th>來源</th>
                  <th>狀態</th>
                  <th>擁有人 / 團隊</th>
                  <th>{sortHeader('加入日期', 'added_asc', 'added_desc')}</th>
                  <th>{sortHeader('到期日', 'expires')}</th>
                  <th>軟體序號</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr className="empty-row"><td colSpan={8}>{list.loading ? '載入中…' : '沒有符合篩選的機器'}</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.sn} className={'clickable' + (selected.has(r.sn) ? ' selected' : '')} onClick={() => lookup.open(r.sn)}>
                      <td className="col-check" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`選取 ${r.sn}`} checked={selected.has(r.sn)} onChange={() => toggle(r.sn)} />
                      </td>
                      <td className="mono">{r.sn}</td>
                      <td><OriginTag origin={r.origin} /></td>
                      <td><StatusText status={r.status} /></td>
                      <td className="ellipsis">{[r.owner_name, r.team].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="muted">{fmtDay(r.added_at)}</td>
                      <td className="muted">{r.expires_at ? utcDay(r.expires_at) : '—'}</td>
                      <td className="mono muted">{r.license_masked || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pager
            page={query.page}
            limit={query.limit}
            total={total}
            unit="台"
            onPage={(p) => setParams({ page: p === 1 ? null : String(p) }, true)}
            onLimit={(l) => setParams({ limit: l === DEFAULT_LIMIT ? null : String(l) })}
          />
        </section>
      </div>

      {addOpen && <AddOneModal onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); list.refresh(); stats.refresh(); }} />}
      {bulkOpen && (
        <BulkRevokeModal
          sns={Array.from(selected)}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setSelected(new Set()); list.refresh(); stats.refresh(); lookup.notifyChanged(); }}
        />
      )}
    </div>
  );
}

function AddOneModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ sn: '', owner_name: '', owner_email: '', team: '', notes: '', expires: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    const sn = f.sn.trim().toUpperCase();
    if (!sn) { setErr('請輸入硬體序號'); return; }
    const exp = f.expires ? dayToUtcMs(f.expires) : null;
    setBusy(true); setErr(null);
    try {
      await api.addWhitelist({
        sn,
        owner_name: f.owner_name.trim() || undefined,
        owner_email: f.owner_email.trim() || undefined,
        team: f.team.trim() || undefined,
        notes: f.notes.trim() || undefined,
        expires_at: exp,
      });
      onDone();
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="新增一台"
      footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" loading={busy} onClick={() => void submit()}>加入</Button></>}
    >
      <TextInput label="硬體序號(BIOS SN)" value={f.sn} onChange={(e) => setF({ ...f, sn: e.target.value })} placeholder="T417NRCX0081MCMB" hint="Win32_BIOS.SerialNumber,存成大寫;來源記為「手動加入」" required autoComplete="off" spellCheck={false} />
      <TextInput label="擁有人" value={f.owner_name} maxLength={200} onChange={(e) => setF({ ...f, owner_name: e.target.value })} />
      <TextInput label="Email" type="email" value={f.owner_email} maxLength={200} onChange={(e) => setF({ ...f, owner_email: e.target.value })} />
      <TextInput label="團隊" value={f.team} maxLength={200} onChange={(e) => setF({ ...f, team: e.target.value })} />
      <TextInput label="到期日(選填)" type="date" value={f.expires} onChange={(e) => setF({ ...f, expires: e.target.value })} />
      <TextArea label="備註" value={f.notes} maxLength={1000} rows={2} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
    </Modal>
  );
}

function BulkRevokeModal({ sns, onClose, onDone }: { sns: string[]; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ revoked: number; missing: string[] } | null>(null);
  const [progress, setProgress] = useState<{ done: number; revoked: number } | null>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    // The Worker takes ≤200 SNs per call: send the selection in sequential pieces and sum them.
    const sum = { revoked: 0, missing: [] as string[] };
    let done = 0;
    try {
      for (const part of chunk(sns, BULK_REVOKE_MAX)) {
        const r = await api.bulkRevokeRoster(part, reason.trim() || undefined);
        sum.revoked += r.revoked;
        sum.missing.push(...r.missing);
        done += part.length;
        if (sns.length > BULK_REVOKE_MAX) setProgress({ done, revoked: sum.revoked });
      }
      setResult(sum);
      onDone();
    } catch (e) {
      // A call that failed mid-way still committed its earlier chunks; the Worker says how many.
      const partial = isApiError(e) && e.code === 'bulk_revoke_incomplete' ? Number(e.details?.revoked ?? 0) : 0;
      sum.revoked += partial;
      if (partial > 0 && done === 0) {
        setErr(`已撤銷 ${sum.revoked} 台後中斷:${errMsg(e)}`);
        onDone();
        return;
      }
      setErr(done > 0 ? `已撤銷 ${sum.revoked} 台後中斷(處理到第 ${done} / ${sns.length} 台):${errMsg(e)}` : errMsg(e));
      if (done > 0) onDone();
    } finally { setBusy(false); }
  };
  return (
    <Modal
      open
      onClose={() => { if (!busy) onClose(); }}
      title="撤銷所選機器"
      footer={
        result ? <Button variant="primary" onClick={onClose}>完成</Button> : (
          <><Button onClick={onClose} disabled={busy}>取消</Button><Button variant="danger" loading={busy} onClick={() => void run()}>撤銷 {sns.length} 台</Button></>
        )
      }
    >
      {result ? (
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <p style={{ margin: 0 }} role="status">已撤銷 <b>{result.revoked}</b> 台。</p>
          {result.missing.length > 0 && (
            <p className="muted-note" style={{ margin: 0 }}>名單上找不到:<span className="mono">{result.missing.join('、')}</span></p>
          )}
        </div>
      ) : (
        <div className="stack" style={{ gap: 'var(--space-3)' }}>
          <p className="subtle" style={{ margin: 0 }}>
            將撤銷選取的 <b>{sns.length}</b> 台。撤銷後這些機器無法通過名單准入,也不能以軟體序號自行恢復,只有後台能復原;每台各寫一筆稽核。
          </p>
          <TextArea label="撤銷原因(選填,寫入稽核)" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          {busy && progress && <div className="muted-note" role="status">撤銷中…已處理 {progress.done} / {sns.length} 台</div>}
          {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
        </div>
      )}
    </Modal>
  );
}
